import { httpApi } from '@app/api/http.api';
import { SearchQueryRequest } from '@app/interfaces/interfaces';
import { IUser } from '@app/interfaces/user';
import { ClientRequest, UpdateUserRequest } from '@app/store/slices/userSlice';
import { encryptField } from '@app/utils/utils';

export interface UsersResponse {
  limit: number;
  page: number;
  results: [];
  totalPages: number;
  totalResults: number;
}

export interface UsersRequest {
  name?: string;
  sortBy?: string;
  projectBy?: string;
  limit?: number;
  page?: number;
}

export const getUser = async (id: string): Promise<IUser> => {
  try {
    const { data } = await httpApi.get<IUser>(`/users/${id}`);
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const createWorker = async (clientPayload: ClientRequest): Promise<undefined> => {
  try {
    const { data } = await httpApi.post<undefined>('/users/create-worker', {
      ...clientPayload,
      password: encryptField(clientPayload.password as string),
    });
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const createAdmin = async (clientPayload: ClientRequest): Promise<undefined> => {
  try {
    const { data } = await httpApi.post<undefined>('/users/create-admin', {
      ...clientPayload,
      password: encryptField(clientPayload.password as string),
    });
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const sendResetPasswordEmail = async (id: string): Promise<undefined> => {
  try {
    const { data } = await httpApi.post<undefined>(`/users/sendResendPasswordEmail/${id}`, {});
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const updateUser = async (clientPayload: UpdateUserRequest): Promise<undefined> => {
  try {
    const { data } = await httpApi.patch<undefined>(`/users/${clientPayload.userId}`, clientPayload.userDetails);
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const updateUserProfile = async (clientPayload: UpdateUserRequest): Promise<undefined> => {
  try {
    const { data } = await httpApi.patch<undefined>(
      `/users/profile/${clientPayload.userId}`,
      clientPayload.userDetails,
    );
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const deleteUser = async (id: string): Promise<undefined> => {
  try {
    const { data } = await httpApi.delete<undefined>(`/users/${id}`);
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const getUserByAuth = async (): Promise<IUser> => {
  try {
    const { data } = await httpApi.get<IUser>(`/users`);
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

const getListWithPagination = async (
  url: string,
  queryPayload: SearchQueryRequest,
): Promise<UsersResponse | IUser[]> => {
  try {
    const { data } = await httpApi.get<UsersResponse | IUser[]>(
      `${url}?${new URLSearchParams(queryPayload as Record<string, string>).toString()}`,
    );
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const getAdminListWithPagination = async (queryPayload: SearchQueryRequest): Promise<UsersResponse> => {
  return getListWithPagination('/users/queryAdminsWithPagination', queryPayload) as Promise<UsersResponse>;
};

export const getWorkerListWithPagination = async (queryPayload: SearchQueryRequest): Promise<UsersResponse> => {
  return getListWithPagination('/users/queryWorkersWithPagination', queryPayload) as Promise<UsersResponse>;
};

export const getUserListWithPagination = async (queryPayload: SearchQueryRequest): Promise<UsersResponse> => {
  return getListWithPagination('/users/queryUsersWithPagination', queryPayload) as Promise<UsersResponse>;
};

export const getUserListWithQuery = async (queryPayload: SearchQueryRequest): Promise<IUser[]> => {
  return getListWithPagination('/users/queryUsers', queryPayload) as Promise<IUser[]>;
};

export const getWorkerListWithQuery = async (queryPayload: SearchQueryRequest): Promise<IUser[]> => {
  return getListWithPagination('/users/queryWorkers', queryPayload) as Promise<IUser[]>;
};

export const getNonWorkerListWithQuery = async (queryPayload: SearchQueryRequest): Promise<IUser[]> => {
  return getListWithPagination('/users/queryNonWorkers', queryPayload) as Promise<IUser[]>;
};

export const verifyEmail = async (token: string): Promise<undefined> => {
  try {
    const { data } = await httpApi.post<undefined>(`/users/verifyEmail/${token}`, {});
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const approveUser = async (id: string): Promise<IUser> => {
  try {
    const { data } = await httpApi.post<IUser>(`/users/approveUser/${id}`, {});
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

export const getTeam = async (): Promise<IUser[]> => {
  try {
    const { data } = await httpApi.get<IUser[]>(`/users/team`);
    return data;
  } catch (error) {
    return await Promise.reject(error);
  }
};
